import React, { useRef, useState } from "react";
import { Copy, Download, Flag } from "lucide-react";
import ReactMarkdown from "react-markdown";
import "../styles/AssistantCard.css";
import AssistantActions from "./AssistantActions";
import ChartIAInsight from "../../../components/Charts/ChartIAInsight";

const SECTION_TITLES = {
  "resumen ejecutivo": "📌 Resumen ejecutivo",
  "kpis principales": "📊 KPIs principales",
  hallazgos: "🔎 Hallazgos",
  "alertas o desvíos": "⚠️ Alertas o desvíos",
  recomendaciones: "✅ Recomendaciones",
  "datos faltantes o supuestos": "🧩 Datos faltantes o supuestos",
};

const normalizeTitle = (title) =>
  title
    .replace(/^#+\s*/, "")
    .trim()
    .toLowerCase();

const normalizeTableLine = (line = "") => {
  const trimmed = line.trim();

  if (!trimmed.includes("|")) return trimmed;

  const hasPipeStart = trimmed.startsWith("|");
  const hasPipeEnd = trimmed.endsWith("|");

  if (hasPipeStart && hasPipeEnd) return trimmed;

  return `| ${trimmed.replace(/^\|/, "").replace(/\|$/, "").trim()} |`;
};

const isTableSeparator = (line = "") => {
  const clean = normalizeTableLine(line);

  if (!clean.includes("|")) return false;

  const cells = clean
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());

  return cells.length > 0 && cells.every((cell) => /^:?-{1,}:?$/.test(cell));
};

const isMarkdownTable = (lines, index) => {
  const current = lines[index]?.trim() || "";
  const next = lines[index + 1]?.trim() || "";

  if (!looksLikeTableRow(current)) return false;

  // Caso Markdown correcto:
  // | A | B |
  // |---|---|
  if (isTableSeparator(next)) return true;

  // Caso que devuelve a veces el agente:
  // | A | B |
  // | dato | dato |
  if (looksLikeTableRow(next)) {
    const currentCells = splitTableRow(current);
    const nextCells = splitTableRow(next);

    return currentCells.length === nextCells.length;
  }

  return false;
};

const looksLikeTableRow = (line = "") => {
  const clean = line.trim();

  if (!clean.includes("|")) return false;
  if (isTableSeparator(clean)) return false;

  const cells = splitTableRow(clean);

  return cells.length >= 2 && cells.some((cell) => cell.trim().length > 0);
};

const splitTableRow = (line) =>
  normalizeTableLine(line)
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());

const parseBlocks = (text = "") => {
  const lines = text.split("\n");
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
if (isMarkdownTable(lines, i)) {
  const tableLines = [lines[i]];
  i += 1;

  // Si viene separador Markdown, saltearlo
  if (i < lines.length && isTableSeparator(lines[i])) {
    i += 1;
  }

  while (i < lines.length && looksLikeTableRow(lines[i])) {
    const rowCells = splitTableRow(lines[i]);
    const headerCells = splitTableRow(tableLines[0]);

    if (rowCells.length !== headerCells.length) {
      break;
    }

    tableLines.push(lines[i]);
    i++;
  }

  blocks.push({
    type: "table",
    header: splitTableRow(tableLines[0]),
    rows: tableLines.slice(1).map(splitTableRow),
  });

  continue;
}

    const headingMatch = lines[i].match(/^##\s+(.+)/);

    if (headingMatch) {
      const rawTitle = headingMatch[1].trim();
      const normalized = normalizeTitle(rawTitle);
      const contentLines = [];
      i++;

      while (
        i < lines.length &&
        !lines[i].match(/^##\s+(.+)/) &&
        !isMarkdownTable(lines, i)
      ) {
        contentLines.push(lines[i]);
        i++;
      }

      blocks.push({
        type: "section",
        title: SECTION_TITLES[normalized] || rawTitle,
        key: normalized,
        content: contentLines.join("\n").trim(),
      });

      continue;
    }

    const textLines = [];

    while (
      i < lines.length &&
      !lines[i].match(/^##\s+(.+)/) &&
      !isMarkdownTable(lines, i)
    ) {
      textLines.push(lines[i]);
      i++;
    }

    const content = textLines.join("\n").trim();

    if (content) {
      blocks.push({
        type: "markdown",
        content,
      });
    }
  }

  return blocks;
};

const extractKpisFromTable = (table) => {
  const firstHeader = table.header?.[0]?.toLowerCase();
  const secondHeader = table.header?.[1]?.toLowerCase();

  const looksLikeKpiTable =
    firstHeader?.includes("concepto") && secondHeader?.includes("valor");

  if (!looksLikeKpiTable) return null;

  return table.rows.map((row) => ({
    label: row[0],
    value: row[1],
  }));
};

const MarkdownTable = ({ header, rows }) => (
  <div className="assistant-table-wrapper">
    <table className="assistant-table">
      <thead>
        <tr>
          {header.map((cell, idx) => (
            <th key={idx} className="assistant-table-header-cell">
              {cell}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIdx) => (
          <tr key={rowIdx} className="assistant-table-row">
            {header.map((_, cellIdx) => (
              <td key={cellIdx} className="assistant-table-cell">
                {row[cellIdx] || ""}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const KpiCards = ({ items }) => (
  <div className="kpis-grid">
    {items.map((kpi, idx) => (
      <div
        key={idx}
        className="kpi-item"
        role="region"
        aria-label={`KPI: ${kpi.label}`}
      >
        <span className="kpi-label">{kpi.label}</span>
        <span className="kpi-value">{kpi.value}</span>
      </div>
    ))}
  </div>
);

/**
 * Extrae acciones recomendadas del texto del mensaje
 * Busca el patrón: "Acciones recomendadas [ {...} ]"
 * Toma la ÚLTIMA ocurrencia si hay múltiples
 */
function extractSuggestedActions(text = "") {
  // Usar global flag para encontrar todas las ocurrencias
  const regex = /Acciones recomendadas\s*(\[[\s\S]*?\])/gi;
  const matches = [...text.matchAll(regex)];

  if (matches.length === 0) {
    return { cleanText: text, actions: [] };
  }

  // Tomar la última ocurrencia
  const lastMatch = matches[matches.length - 1];

  try {
    const actions = JSON.parse(lastMatch[1]);

    // Reemplazar solo la última ocurrencia usando índices
    const startIndex = lastMatch.index;
    const endIndex = lastMatch.index + lastMatch[0].length;
    const cleanText = (
      text.substring(0, startIndex) + text.substring(endIndex)
    ).trim();

    return {
      cleanText,
      actions: Array.isArray(actions) ? actions : [],
    };
  } catch (error) {
    console.error("No se pudieron parsear las acciones recomendadas:", error);
    return { cleanText: text, actions: [] };
  }
}

const slugify = (value = "") =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const AssistantMarkdownContent = ({ content, reportRef }) => {
  const blocks = parseBlocks(content);

  return (
    <div className="assistant-rich-content" ref={reportRef}>
      {blocks.map((block, idx) => {
        if (block.type === "table") {
          const previousBlock = blocks[idx - 1];

          if (previousBlock?.type === "section") {
            return null;
          }

          return (
            <MarkdownTable key={idx} header={block.header} rows={block.rows} />
          );
        }

        if (block.type === "section") {
          const nextBlock = blocks[idx + 1];
          const hasTableAfter = nextBlock?.type === "table";
          const kpis = hasTableAfter ? extractKpisFromTable(nextBlock) : null;

          return (
            <div
              key={idx}
              className={`assistant-card-section section-${slugify(block.key)}`}
            >
              <h4 className="assistant-card-section-title">{block.title}</h4>

              {block.content && (
                <div className="assistant-section-content">
                  <ReactMarkdown>{block.content}</ReactMarkdown>
                </div>
              )}

              {hasTableAfter &&
                (kpis ? (
                  <KpiCards items={kpis} />
                ) : (
                  <MarkdownTable
                    header={nextBlock.header}
                    rows={nextBlock.rows}
                  />
                ))}
            </div>
          );
        }

        return (
          <div key={idx} className="assistant-card-summary">
            <ReactMarkdown>{block.content}</ReactMarkdown>
          </div>
        );
      })}
    </div>
  );
};

const AssistantCard = ({ message, showActions = false, onSuggestedAction }) => {
  const [copyFeedback, setCopyFeedback] = useState(false);
  const data = message.content;
  const artifacts = Array.isArray(data.artifacts) ? data.artifacts : [];
  const charts = artifacts.filter((artifact) => artifact.type === "chart");
  const reportRef = useRef(null);

  // Extraer acciones recomendadas del mensaje como fallback para respuestas antiguas
  const extractedSuggestedActions = extractSuggestedActions(data.message || "");
  const cleanText = extractedSuggestedActions.cleanText;
  const actions =
    Array.isArray(data.actions) && data.actions.length > 0
      ? data.actions
      : extractedSuggestedActions.actions;
  const parsedTables = parseBlocks(cleanText).filter(
    (block) => block.type === "table",
  );

  const handleCopy = () => {
    const text = formatTextContent(data);
    navigator.clipboard.writeText(text);
    setCopyFeedback(true);
    setTimeout(() => setCopyFeedback(false), 2000);
  };
  const formatTextContent = (data) => {
    let text = "";

    // Extraer acciones recomendadas del mensaje
    const { cleanText: messageClean } = extractSuggestedActions(
      data.message || "",
    );

    // Si solo hay mensaje simple, lo devolvemos
    if (
      messageClean &&
      !data.kpis &&
      !data.observations &&
      !data.recommendations
    ) {
      return messageClean;
    }

    if (data.title) text += `${data.title}\n`;
    if (messageClean) text += `\n${messageClean}\n`;
    if (data.kpis && data.kpis.length > 0) {
      text += "\nKPIs destacados:\n";
      data.kpis.forEach((kpi) => {
        text += `- ${kpi.label}: ${kpi.value}\n`;
      });
    }
    if (data.observations && data.observations.length > 0) {
      text += "\nObservaciones:\n";
      data.observations.forEach((obs) => {
        text += `- ${obs}\n`;
      });
    }
    if (data.recommendations && data.recommendations.length > 0) {
      text += "\nRecomendaciones:\n";
      data.recommendations.forEach((rec) => {
        text += `- ${rec}\n`;
      });
    }
    return text;
  };

  const isPending = message.status !== "done";

  return (
    <div className={`assistant-card ${message.status}`}>
      {data.title && <h3 className="assistant-card-title">{data.title}</h3>}

      {cleanText ? (
        <AssistantMarkdownContent content={cleanText} reportRef={reportRef} />
      ) : (
        <p
          className="assistant-card-summary"
          style={{ opacity: 0.7, fontStyle: "italic" }}
        >
          {isPending
            ? "SIGERBot está generando tu informe..."
            : "⏳ Procesando respuesta..."}
        </p>
      )}

      {charts.length > 0 && (
        <div className="assistant-charts-list">
          {charts.map((chart, idx) => (
            <ChartIAInsight key={idx} chart={chart} />
          ))}
        </div>
      )}

      {/* Renderizar botones de acciones recomendadas */}
      {actions.length > 0 && (
        <div className="assistant-suggested-actions">
          <div className="assistant-suggested-actions-title">
            Acciones recomendadas
          </div>
          <div className="assistant-suggested-actions-list">
            {actions.map((action, idx) => (
              <button
                key={idx}
                type="button"
                className="assistant-suggested-action-button"
                onClick={() =>
                  onSuggestedAction?.(
                    `Respecto a la información anterior, ${action.prompt}`
                  )
                }
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {showActions && (
        <AssistantActions
          content={cleanText}
          tables={parsedTables}
          charts={charts}
          reportRef={reportRef}
          onCopy={handleCopy}
          copyFeedback={copyFeedback}
        />
      )}
    </div>
  );
};

export default AssistantCard;
